import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { Link as RouterLink } from "react-router-dom";

import { formatListDate, formatWeekdayFull } from "@/utils/date-format";
import { staffShowPath } from "@/containers/staff/staff/staff-show-helpers";

import {
  compactStatusSymbol,
  formatAttendanceHoursLabel,
  formatAttendanceTimeLocal,
} from "../attendance-cell-display";
import type { DayDrilldown } from "../attendance-types";

type Props = {
  open: boolean;
  drilldown: DayDrilldown | null;
  month: string;
  timezone: string;
  onClose: () => void;
  /** When true (default), show link to the per-staff dashboard. */
  showStaffDashboardLink?: boolean;
};

function statusLabel(status: string): string {
  switch (status) {
    case "PRESENT":
      return "Present";
    case "INCOMPLETE":
      return "Incomplete";
    case "ABSENT":
      return "Absent";
    case "OFF":
      return "Off day";
    case "HOLIDAY":
      return "Office holiday";
    case "LEAVE":
      return "Approved leave";
    case "LEAVE_PENDING":
      return "Pending leave";
    default:
      return status;
  }
}

export function AttendanceDayDrawer({
  open,
  drilldown,
  month,
  timezone,
  onClose,
  showStaffDashboardLink = true,
}: Props) {
  const day = drilldown?.day;
  const cell = drilldown?.cell;
  const date = day != null ? dayjs(`${month}-${String(day).padStart(2, "0")}`) : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 360 } } }}
    >
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6" component="h2">
            Day detail
          </Typography>
          <IconButton aria-label="Close" onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Stack>

        {drilldown && cell && date ? (
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="subtitle1">{drilldown.employee.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Id {drilldown.employee.employeeId}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body1">
                {formatWeekdayFull(date)} · {formatListDate(date)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body1">
                {compactStatusSymbol(cell.status)} — {statusLabel(cell.status)}
                {cell.late ? " (late)" : ""}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Check-in / Check-out
              </Typography>
              <Typography variant="body1">
                {formatAttendanceTimeLocal(cell.checkIn, timezone)} –{" "}
                {formatAttendanceTimeLocal(cell.checkOut, timezone)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Hours
              </Typography>
              <Typography variant="body1">{formatAttendanceHoursLabel(cell.hours)}</Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Raw punches ({timezone})
              </Typography>
              {cell.punches.length === 0 ? (
                <Typography variant="body2">No punches recorded.</Typography>
              ) : (
                <List dense disablePadding>
                  {cell.punches.map((punch) => (
                    <ListItem key={punch} disableGutters>
                      <ListItemText
                        primary={formatAttendanceTimeLocal(punch, timezone)}
                        secondary={punch}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {showStaffDashboardLink ? (
              <Button
                component={RouterLink}
                to={staffShowPath(drilldown.employee.employeeId, month)}
                variant="outlined"
                onClick={onClose}
              >
                Open staff dashboard
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Box>
    </Drawer>
  );
}
